// frontend/src/hooks/useMyLinkJobPopup.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MyLinkJobPopup, { 
  saveUnprocessedJob, 
  getUnprocessedJobs, 
  removeUnprocessedJob 
} from '../components/MyLinkJobPopup';

// Helper functions to manage dismissed jobs in localStorage
const getDismissedJobInfo = () => {
  try {
    const stored = localStorage.getItem('mylink_dismissed_job');
    if (!stored) return null;
    const data = JSON.parse(stored);
    // Check if cooldown period has passed (30 seconds)
    const timeSinceDismissal = Date.now() - (data.dismissedAt || 0);
    if (timeSinceDismissal >= 30000) {
      // Cooldown expired, clear it
      localStorage.removeItem('mylink_dismissed_job');
      return null;
    }
    return data;
  } catch (error) {
    console.error('Failed to get dismissed job info:', error);
    return null;
  }
};

const setDismissedJobInfo = (jobId) => {
  try {
    localStorage.setItem('mylink_dismissed_job', JSON.stringify({
      jobId,
      dismissedAt: Date.now()
    }));
  } catch (error) {
    console.error('Failed to set dismissed job info:', error);
  }
};

const clearDismissedJobInfo = () => {
  try {
    localStorage.removeItem('mylink_dismissed_job');
  } catch (error) {
    console.error('Failed to clear dismissed job info:', error);
  }
};

export default function useMyLinkJobPopup() {
  const [currentJob, setCurrentJob] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [allUnprocessedJobs, setAllUnprocessedJobs] = useState([]);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);

  // Check for MyLink jobs in the diary (passed via event)
  const checkMyLinkJobsInDiary = useCallback((jobs) => {
    try {
      console.log('🔍 Checking for MyLink jobs in diary...');
      console.log(`📋 Checking ${jobs.length} job(s) from diary`);
      
      if (!jobs || jobs.length === 0) {
        console.log('✅ No MyLink jobs found in diary');
        setIsOpen(false);
        setCurrentJob(null);
        return;
      }
      
      // Get the most recent job
      const mostRecent = jobs.sort((a, b) => {
        const dateA = new Date(a.dateTime || 0);
        const dateB = new Date(b.dateTime || 0);
        return dateB - dateA;
      })[0];
      
      const mostRecentJobId = mostRecent._id || mostRecent.leadId;
      
      // Check if this job was just dismissed (within last 30 seconds)
      const dismissedInfo = getDismissedJobInfo();
      if (dismissedInfo && dismissedInfo.jobId === mostRecentJobId) {
        const timeSinceDismissal = Date.now() - dismissedInfo.dismissedAt;
        const remainingSeconds = Math.ceil((30000 - timeSinceDismissal) / 1000);
        console.log('⏸️ Skipping popup - job was just dismissed', remainingSeconds, 'seconds ago (cooldown active)');
        return;
      }
      
      console.log('📋 Most recent MyLink job from diary:', {
        leadId: mostRecentJobId,
        from: mostRecent.from,
        to: mostRecent.to,
        customerName: mostRecent.customerName
      });
      
      // Prepare job data for popup
      const jobToShow = {
        leadId: mostRecent._id || mostRecent.leadId,
        _id: mostRecent._id || mostRecent.leadId,
        from: mostRecent.from,
        to: mostRecent.to,
        customerName: mostRecent.customerName,
        passengers: mostRecent.passengers,
        dateTime: mostRecent.dateTime,
        isASAP: mostRecent.isASAP,
        notes: mostRecent.notes || '',
        bookingLinkId: mostRecent.bookingLinkId
      };
      
      // Get all MyLink jobs for navigation
      const allJobs = jobs.sort((a, b) => {
        const dateA = new Date(a.dateTime || 0);
        const dateB = new Date(b.dateTime || 0);
        return dateB - dateA;
      });
      
      // Find the index of the current job
      const jobIndex = allJobs.findIndex(j => 
        (j._id || j.leadId) === (jobToShow.leadId || jobToShow._id)
      );
      
      // Show the popup with the job data
      console.log('✅ Setting job and opening popup:', {
        leadId: jobToShow.leadId || jobToShow._id,
        from: jobToShow.from,
        to: jobToShow.to,
        index: jobIndex,
        total: allJobs.length
      });
      setAllUnprocessedJobs(allJobs.map(j => ({
        leadId: j._id || j.leadId,
        _id: j._id || j.leadId,
        from: j.from,
        to: j.to,
        customerName: j.customerName,
        passengers: j.passengers,
        dateTime: j.dateTime,
        isASAP: j.isASAP,
        notes: j.notes || '',
        bookingLinkId: j.bookingLinkId
      })));
      setCurrentJobIndex(jobIndex >= 0 ? jobIndex : 0);
      setCurrentJob(jobToShow);
      setIsOpen(true);
      console.log('✅ Popup should now be visible');
    } catch (error) {
      console.error('❌ Failed to check MyLink jobs in diary:', error);
    }
  }, []);

  // Check for unprocessed jobs and show popup if any exist (legacy - for localStorage jobs)
  const checkUnprocessedJobs = useCallback(async () => {
    try {
      console.log('🔍 Checking for unprocessed MyLink jobs in localStorage...');
      const unprocessed = getUnprocessedJobs();
      console.log(`📋 Found ${unprocessed.length} unprocessed job(s) in localStorage`);
      
      if (unprocessed.length === 0) {
        console.log('✅ No unprocessed jobs found in localStorage');
        // Don't close popup if it's already open from diary check
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (!currentJob) {
          setIsOpen(false);
          setCurrentJob(null);
        }
        return;
      }

      // Get the most recent unprocessed job
      const mostRecent = unprocessed.sort((a, b) => {
        const dateA = new Date(a.receivedAt || 0);
        const dateB = new Date(b.receivedAt || 0);
        return dateB - dateA;
      })[0];

      const mostRecentJobId = mostRecent.leadId || mostRecent._id;
      
      // Check if this job was just dismissed (within last 30 seconds) - check localStorage
      const dismissedInfo = getDismissedJobInfo();
      if (dismissedInfo && dismissedInfo.jobId === mostRecentJobId) {
        const timeSinceDismissal = Date.now() - dismissedInfo.dismissedAt;
        const remainingSeconds = Math.ceil((30000 - timeSinceDismissal) / 1000);
        console.log('⏸️ Skipping popup - job was just dismissed', remainingSeconds, 'seconds ago (cooldown active)');
        return;
      }

      console.log('📋 Most recent unprocessed job:', {
        leadId: mostRecentJobId,
        from: mostRecent.from,
        to: mostRecent.to,
        customerName: mostRecent.customerName
      });

      // Try to verify the job, but show popup even if verification fails
      let jobToShow = mostRecent;
      
      try {
        console.log('🔍 Verifying job with server...');
        const response = await axios.get(`/view-leads`);
        const allJobs = response.data;
        console.log(`📋 Server returned ${allJobs.length} jobs`);
        
        // Find the job in the current jobs list - be more lenient with matching
        const existingJob = allJobs.find(j => {
          const jobId = j._id?.toString();
          const storedId = (mostRecent.leadId || mostRecent._id)?.toString();
          // Match by ID and check if it's a MyLink job (has bookingLinkId)
          const isMatch = jobId === storedId;
          const isMyLinkJob = j.bookingLinkId;
          const notAccepted = !j.acceptedAt;
          console.log('🔍 Job match check:', {
            jobId,
            storedId,
            isMatch,
            hasBookingLinkId: isMyLinkJob,
            notAccepted,
            status: j.status
          });
          return isMatch && isMyLinkJob && notAccepted;
        });

        if (existingJob) {
          console.log('✅ Job verified - exists and not accepted');
          // Job still exists and hasn't been accepted - use updated data
          jobToShow = {
            ...mostRecent,
            leadId: existingJob._id,
            _id: existingJob._id,
            from: existingJob.from || mostRecent.from,
            to: existingJob.to || mostRecent.to,
            customerName: existingJob.customerName || mostRecent.customerName,
            passengers: existingJob.passengers || mostRecent.passengers,
            dateTime: existingJob.dateTime || mostRecent.dateTime,
            isASAP: existingJob.isASAP || mostRecent.isASAP,
            notes: existingJob.notes || mostRecent.notes,
            bookingLinkId: existingJob.bookingLinkId || mostRecent.bookingLinkId
          };
        } else {
          // Check if job exists but was accepted - only then remove it
          const jobExistsButAccepted = allJobs.find(j => {
            const jobId = j._id?.toString();
            const storedId = (mostRecent.leadId || mostRecent._id)?.toString();
            return jobId === storedId && j.acceptedAt;
          });
          
          if (jobExistsButAccepted) {
            // Job was accepted - remove from unprocessed
            console.log('✅ Job was accepted - removing from unprocessed');
            removeUnprocessedJob(mostRecent.leadId || mostRecent._id);
            // Check for next unprocessed job
            setTimeout(() => {
              checkUnprocessedJobs();
            }, 100);
            return;
          } else {
            // Job not found in server response - might be a timing issue
            // Still show the popup with stored data (don't remove it)
            console.log('⚠️ Job not found in server response - showing with stored data (may be timing issue)');
            jobToShow = mostRecent;
          }
        }
      } catch (error) {
        console.error('⚠️ Failed to verify unprocessed job:', error);
        console.log('⚠️ Showing popup with stored data (verification failed)');
        // Still show the popup if we can't verify - use stored data
        jobToShow = mostRecent;
      }

      // Get all unprocessed jobs for navigation
      const allJobs = unprocessed.sort((a, b) => {
        const dateA = new Date(a.receivedAt || 0);
        const dateB = new Date(b.receivedAt || 0);
        return dateB - dateA;
      });
      
      // Find the index of the current job
      const jobIndex = allJobs.findIndex(j => 
        (j.leadId || j._id) === (jobToShow.leadId || jobToShow._id)
      );
      
      // Show the popup with the job data
      console.log('✅ Setting job and opening popup:', {
        leadId: jobToShow.leadId || jobToShow._id,
        from: jobToShow.from,
        to: jobToShow.to,
        index: jobIndex,
        total: allJobs.length
      });
      setAllUnprocessedJobs(allJobs);
      setCurrentJobIndex(jobIndex >= 0 ? jobIndex : 0);
      setCurrentJob(jobToShow);
      setIsOpen(true);
      console.log('✅ Popup should now be visible');
    } catch (error) {
      console.error('❌ Failed to check unprocessed jobs:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removed dependencies - checkUnprocessedJobs is self-contained

  // Handle job acceptance
  const handleAccept = useCallback(async (jobId) => {
    try {
      await axios.put(`/leads/${jobId}`, {
        dateAccepted: new Date().toISOString()
      });
      
      // Remove from unprocessed jobs
      removeUnprocessedJob(jobId);
      
      // Clear dismissed job info if this job was dismissed
      clearDismissedJobInfo();
      
      // Update the allUnprocessedJobs list
      const updatedJobs = allUnprocessedJobs.filter(j => 
        (j.leadId || j._id) !== jobId
      );
      setAllUnprocessedJobs(updatedJobs);
      
      // If there are more jobs, navigate to the next one
      if (updatedJobs.length > 0) {
        const nextIndex = currentJobIndex < updatedJobs.length ? currentJobIndex : 0;
        const nextJob = updatedJobs[nextIndex];
        setCurrentJobIndex(nextIndex);
        setCurrentJob(nextJob);
        console.log('✅ Job accepted, moving to next job:', nextIndex + 1, 'of', updatedJobs.length);
      } else {
        // No more jobs, close popup
        setIsOpen(false);
        setCurrentJob(null);
        setAllUnprocessedJobs([]);
        setCurrentJobIndex(0);
        console.log('✅ Job accepted, no more jobs to review');
      }
      
      // Emit refresh event
      window.dispatchEvent(new CustomEvent('refreshDiaryData', {
        detail: { leadId: jobId, action: 'accepted' }
      }));
    } catch (error) {
      console.error('Failed to accept job:', error);
      throw error;
    }
  }, [allUnprocessedJobs, currentJobIndex]);

  // Navigation removed - accordion handles job navigation

  // Handle review later - close popup and prevent immediate reopening
  const handleReviewLater = useCallback(() => {
    if (currentJob) {
      const jobId = currentJob.leadId || currentJob._id;
      console.log('⏸️ Review Later clicked - closing popup and preventing immediate reopen for job:', jobId);
      // Store dismissed job info in localStorage (persists across refreshes)
      setDismissedJobInfo(jobId);
      console.log('💾 Dismissed job info saved to localStorage - will reappear after 30 seconds or on refresh');
    }
    setIsOpen(false);
    setCurrentJob(null);
    setAllUnprocessedJobs([]);
    setCurrentJobIndex(0);
  }, [currentJob]);

  // Check on mount and every 5 minutes
  useEffect(() => {
    console.log('🚀 useMyLinkJobPopup useEffect running');
    const myId = localStorage.getItem('userId');
    const userRole = localStorage.getItem('role'); // Fixed: use 'role' not 'userRole'
    
    console.log('👤 Checking user credentials - ID:', myId, 'Role:', userRole);
    
    // Only run for professional drivers
    if (!myId || userRole !== 'professional') {
      console.log('⚠️ Skipping popup setup - user not a professional driver. ID:', myId, 'Role:', userRole);
      return;
    }
    
    console.log('✅ User is professional driver - setting up popup system');

    // Check immediately on mount (page load/refresh)
    console.log('🔄 Page loaded - checking for unprocessed MyLink jobs');
    console.log('👤 User ID:', myId, 'Role:', userRole);
    
    // Test localStorage access
    try {
      const testData = getUnprocessedJobs();
      console.log('💾 localStorage test - unprocessed jobs:', testData.length);
    } catch (e) {
      console.error('❌ localStorage access failed:', e);
    }
    
    // Small delay to ensure page is fully loaded
    // Check for unprocessed jobs on mount/refresh
    setTimeout(() => {
      console.log('⏰ Running checkUnprocessedJobs after delay (page refresh/load)');
      // Always check on mount/refresh - don't skip if popup is open (it shouldn't be on initial load)
      checkUnprocessedJobs();
    }, 1500);

    // Set up interval to check every 5 minutes
    const interval = setInterval(() => {
      console.log('⏰ Checking for unprocessed MyLink jobs (5-minute interval)');
      checkUnprocessedJobs();
    }, 5 * 60 * 1000); // 5 minutes

    // Also check when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ Page visible - checking for unprocessed MyLink jobs');
        checkUnprocessedJobs();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for new booking requests
    const handleNewBookingRequest = (event) => {
      console.log('🎯 handleNewBookingRequest called with event:', event);
      const jobData = event.detail;
      console.log('📋 Job data from event:', jobData);
      
      if (jobData && jobData.leadId) {
        console.log('🔔 New MyLink booking request received:', jobData);
        saveUnprocessedJob(jobData);
        
        // Show popup immediately with the job data from the event
        // Don't wait for verification - show it right away
        const jobToShow = {
          ...jobData,
          leadId: jobData.leadId,
          _id: jobData.leadId || jobData._id
        };
        
        console.log('✅ Setting current job and opening popup:', jobToShow);
        setCurrentJob(jobToShow);
        setIsOpen(true);
        console.log('✅ Popup state set - isOpen should be true, currentJob set');
      } else {
        console.error('❌ Invalid job data in event:', jobData);
      }
    };
    
    // Listen for MyLink jobs found in diary
    const handleMyLinkJobsInDiary = (event) => {
      console.log('📋 MyLink jobs found in diary:', event.detail);
      const { jobs } = event.detail;
      if (jobs && jobs.length > 0) {
        checkMyLinkJobsInDiary(jobs);
      }
    };
    
    console.log('👂 Registering event listeners for MyLink jobs');
    window.addEventListener('mylink-new-booking-request', handleNewBookingRequest);
    window.addEventListener('mylink-jobs-in-diary', handleMyLinkJobsInDiary);

    // Listen for job acceptance events to remove from unprocessed
    const handleJobAccepted = (event) => {
      const jobId = event.detail?.leadId || event.detail?.jobId;
      if (jobId) {
        console.log('✅ Job accepted - removing from unprocessed:', jobId);
        removeUnprocessedJob(jobId);
        // Clear dismissed job info if this job was dismissed
        clearDismissedJobInfo();
        // Always check for next unprocessed job after removing one
        setTimeout(() => {
          checkUnprocessedJobs();
        }, 500);
      }
    };
    window.addEventListener('refreshDiaryData', handleJobAccepted);

    // Listen for manual check request (e.g., from My Diary header click)
    const handleManualCheck = () => {
      console.log('🔄 Manual MyLink job check requested');
      checkUnprocessedJobs();
    };
    window.addEventListener('check-mylink-jobs', handleManualCheck);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mylink-new-booking-request', handleNewBookingRequest);
      window.removeEventListener('mylink-jobs-in-diary', handleMyLinkJobsInDiary);
      window.removeEventListener('refreshDiaryData', handleJobAccepted);
      window.removeEventListener('check-mylink-jobs', handleManualCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkUnprocessedJobs, checkMyLinkJobsInDiary]); // checkMyLinkJobsInDiary is stable

  // Debug logging for popup state
  useEffect(() => {
    console.log('🔍 Popup state changed:', {
      isOpen,
      hasJob: !!currentJob,
      jobId: currentJob?.leadId || currentJob?._id,
      jobFrom: currentJob?.from,
      jobTo: currentJob?.to
    });
    
    if (isOpen && currentJob) {
      console.log('✅ Popup should be visible with job:', currentJob.leadId || currentJob._id);
    } else if (currentJob && !isOpen) {
      console.log('⚠️ Popup has job but isOpen is false - popup will not render');
    } else if (isOpen && !currentJob) {
      console.log('⚠️ Popup isOpen is true but no job data - popup will not render');
    } else {
      console.log('ℹ️ No popup - isOpen:', isOpen, 'hasJob:', !!currentJob);
    }
  }, [isOpen, currentJob]);

  // Render popup component
  const PopupComponent = isOpen && allUnprocessedJobs.length > 0 ? (
    <MyLinkJobPopup
      allJobs={allUnprocessedJobs}
      onAccept={handleAccept}
      onReviewLater={handleReviewLater}
      onClose={handleReviewLater}
    />
  ) : null;
  
  // Debug logging in useEffect to avoid render issues
  useEffect(() => {
    console.log('🎨 PopupComponent render check:', {
      willRender: !!(isOpen && allUnprocessedJobs.length > 0),
      isOpen,
      jobCount: allUnprocessedJobs.length
    });
  }, [isOpen, allUnprocessedJobs.length]);

  return {
    PopupComponent,
    checkUnprocessedJobs,
    handleAccept
  };
}

